import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DmsModal, DmsButton, DmsTextField, intl, fetchData } from "dmsbasereact";
import { Box, useMediaQuery, useTheme, Grid } from "@mui/material";
import Swal from "sweetalert2";
import { PrescriptionReason } from "../../constants/PrescriptionReason";

const PrescriptionReasonFormModal = ({
    isVisible,
    onClose,
    onSave = null,
    reason = null,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isEditMode = Boolean(reason);

    const [formName, setFormName] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isVisible) {
            if (reason) {
                // Use translated values for display in edit mode
                setFormName(PrescriptionReason.getText(reason.name));
                setFormDescription(PrescriptionReason.getText(reason.name));
            } else {
                setFormName("");
                setFormDescription("");
            }
        }
    }, [isVisible, reason]);

    const handleSave = async () => {
        if (!formName.trim()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("ReasonNameRequired").d("Reason name is required."),
            });
            return;
        }

        const confirmResult = await Swal.fire({
            icon: "question",
            title: intl.get("ConfirmAction").d("Confirm Action"),
            text: isEditMode
                ? intl.get("ConfirmUpdatePrescriptionReason").d("Are you sure you want to update this prescription reason?")
                : intl.get("ConfirmCreatePrescriptionReason").d("Are you sure you want to create this prescription reason?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        setSaving(true);
        try {
            const payload = {
                // In edit mode, keep the original name (enum key); in create mode, use the form value
                name: isEditMode ? reason.name : formName.trim(),
                description: formDescription.trim() || null,
            };

            if (isEditMode) {
                await fetchData(`/prescription_reason/${reason.pk}`, "PUT", true, payload);
            } else {
                await fetchData("/prescription_reason", "POST", true, payload);
            }

            await Swal.fire({
                icon: "success",
                title: intl.get("Success").d("Success"),
                text: isEditMode
                    ? intl.get("PrescriptionReasonUpdatedSuccessfully").d("Prescription reason updated successfully")
                    : intl.get("PrescriptionReasonCreatedSuccessfully").d("Prescription reason created successfully"),
            });

            if (onSave) onSave();
            onClose();
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToSavePrescriptionReason").d("Failed to save prescription reason"),
            });
        } finally {
            setSaving(false);
        }
    };

    const title = isEditMode
        ? intl.get("EditPrescriptionReason").d("Edit Prescription Reason")
        : intl.get("CreatePrescriptionReason").d("Create Prescription Reason");

    const isFormValid = formName.trim().length > 0;

    return (
        <DmsModal
            isVisible={isVisible}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header title={title} onClose={onClose} />
            <DmsModal.Body>
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="name"
                                label={intl.get("ReasonName").d("Reason Name")}
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="description"
                                label={intl.get("Description").d("Description")}
                                value={formDescription}
                                onChange={(e) => setFormDescription(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "flex-end",
                        gap: 1,
                        width: "100%",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={onClose}
                        fullWidth={isMobile}
                        disabled={saving}
                    />
                    <DmsButton
                        variant="primary"
                        text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                        onClick={handleSave}
                        fullWidth={isMobile}
                        disabled={!isFormValid || saving}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

PrescriptionReasonFormModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    reason: PropTypes.object,
};

export { PrescriptionReasonFormModal };
